/* eslint-disable camelcase */
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import FormSelect from '../../../components/dropdown/formSelect';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import CustomTextArea from '../../../components/text-area/textArea';
import {
  VestingTemplateService,
  type Wealthlane_AwardService_Dtos_VestingTemplate_UpdateVestingTemplateDto,
} from '../../../services/wealthlane-award-services';
import { type ModalStatusType } from '../../../types/common';
import { VESTING_TEMPLATES_VALIDATION } from '../../../utils/validations/validations';
import { FormDivider } from '../system-roles/roles.styled';
import { setObjectNullValue } from '../../../utils/global';
import { DATE_FORMAT, VestingFrequencyOptions } from '../../../constants/common';
import ViewUploadedDoc from '../../../components/view-uploaded-doc';
import { VestingModalWrapper } from './vestingTemplates.styled';
import Heading from '../../../components/typography';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import ToastComponent from '../../../components/toast';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import { useGetEndOfMonthTypes, useGetRoundingType, useWealthlaneAwardApi } from '../../../hooks';
import _, { isEmpty } from 'lodash';
import CustomDatePicker from '../../../components/date-picker/customDatePicker';
import CustomCheckBox from '../../../components/radio-button/customCheckBox';
import { commonValidation } from '../../../utils/validations/validationRule';
import moment from 'moment';

import { ACCEPT_DECIMAL } from '../../../utils/apiDecimalConfig';
interface VestingModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  modalLoading: boolean;
  vestingTypeOptions?: Array<{
    label: string;
    value: string;
    isCliffVesting: boolean | undefined;
    isStepVesting: boolean | undefined;
    isHybridVesting: boolean | undefined;
  }>;
  loadingTypeOptions?: Array<{
    label: string;
    value: string | number;
  }>;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  setPreviewData?: () => void;
  setEditedValue?: (value: any) => void;
  rowData?: any;
  id?: string;
}

const NewVestingModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  initialData,
  modalLoading,
  vestingTypeOptions,
  loadingTypeOptions,
  setModalStatus,
  setPreviewData,
  setEditedValue,
  rowData,
  id,
}: VestingModalType): JSX.Element => {
  const [percentageOne, setPercentageOne] = useState<number>(0);
  const [percentage, setPercentage] = useState<number>(0);
  const queryClient = useQueryClient();
  const [validationState, setValidationState] = useState<any>(VESTING_TEMPLATES_VALIDATION);
  const { handleRequest } = useWealthlaneAwardApi();
  const { data: endOfMonthTypesOptions, isLoading: endOfMonthTypesLoading } =
    useGetEndOfMonthTypes(handleRequest);

  const useSubmitVestingTemplates = (): any => {
    return useMutation(
      async (payload: Wealthlane_AwardService_Dtos_VestingTemplate_UpdateVestingTemplateDto) => {
        const newValue: any = setObjectNullValue(payload);
        window?.sessionStorage.setItem('ToPreviewData', JSON.stringify(newValue));
        setEditedValue && setEditedValue(newValue);
        if (initialData.id != null && initialData.id !== '' && modalStatus === 'Edit') {
          return await handleRequest(
            VestingTemplateService.putApiAwardApiAppVestingTemplateVestingTemplate({
              ...newValue,
              id: initialData.id,
            })
          );
        }
        return await handleRequest(
          VestingTemplateService.postApiAwardApiAppVestingTemplateVestingTemplate(newValue)
        );
      },
      {
        onSuccess: (response, resetForm: any) => {
          if (response != null) {
            void queryClient.invalidateQueries('getVestingTemplateList');
            void queryClient.invalidateQueries('getGrantVestingTemplateList');
            closeModal();
            setPreviewData && setPreviewData();
            toast.success(
              <ToastComponent
                label='Success'
                message={`Vesting template ${
                  initialData.id != null && initialData.id !== '' && modalStatus === 'Edit'
                    ? 'edited'
                    : 'added'
                } `}
              />
            );
            resetForm();
          }
        },
        onError: (err) => {
          console.log('error', err);
        },
      }
    );
  };

  const { mutate, isLoading } = useSubmitVestingTemplates();

  const handleSubmit = (values: any): void => {
    const vestsInMonths = Number(values.lengthInMonths) - Number(values.vestsAfterInMonths);

    const payload = {
      intervalInMonths: Number(values.intervalInMonths),
      amountOneInPercentage:
        values?.amountOneInPercentage !== '' ? values?.amountOneInPercentage : null,
      // amountTwoInPercentage: percentage ?? 0,
      lengthInMonths:
        vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)?.label ===
        'Specific Vesting'
          ? '1'
          : values.lengthInMonths != ''
          ? Number(values.lengthInMonths)
          : null,
      name: values.name,
      summary: values.summary ?? null,
      vestingTypeId: values.vestingTypeId ?? null,
      vestingType:
        vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)?.label ?? null,
      vestsAfterInMonths:
        values.vestsAfterInMonths != null && values.vestsAfterInMonths !== ''
          ? Number(values.vestsAfterInMonths)
          : null,
      // planTypeCategoryId: values.planTypeCategoryId,
      roundingTypeId: vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
        ?.isCliffVesting
        ? 3
        : values?.roundingTypeId ?? null,
      specificVestingInstruction:
        vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)?.label ===
        'Specific Vesting'
          ? values?.specificVestingInstruction
          : null,
      periodStartDate: values?.periodStartDate ? values?.periodStartDate : null,
      vestAtEndOfMonth: values?.vestAtEndOfMonth ?? false,
      vestsInMonths: vestsInMonths ?? 0,
      fractionalDecimalDigit: 0,
      loadingTypeId: values?.loadingTypeId ?? null,
      endOfTheMonthTypeId: values?.endOfTheMonthTypeId ? values?.endOfTheMonthTypeId : 1,
    };

    mutate(payload as any);
  };

  useEffect(() => {
    if (initialData.data as boolean) {
      const { vestsAfterInMonths, lengthInMonths, intervalInMonths, amountOneInPercentage } =
        initialData.data;

      handlePercentageOne(vestsAfterInMonths, lengthInMonths);
      handlePercentageTwo(intervalInMonths, lengthInMonths);
      handlePercentageThree(
        intervalInMonths,
        lengthInMonths,
        Number(Number(amountOneInPercentage * 100).toFixed(ACCEPT_DECIMAL)),
        vestsAfterInMonths
      );
    }
  }, [initialData.data]);

  const handlePercentageOne = (value: string, lengthInMonths: number): void => {
    if (value != null && Number(value) > 0) {
      const calcValue = (Number(value) / Number(lengthInMonths)) * 100 ?? 0;

      setPercentageOne(Number(calcValue));
    }
  };

  const handlePercentageTwo = (value: string, lengthInMonths: number): void => {
    if (value != null && Number(value) > 0) {
      const newValue = (Number(value) / Number(lengthInMonths)) * 100 ?? 0;
      setPercentage(Number(newValue));
    }
  };
  const handlePercentageThree = (
    value: number,
    lengthInMonths: number,
    amountOneInPercentage: number,
    vestsAfterInMonths: number
  ): void => {
    const remainingPercentage = 100 - Number(amountOneInPercentage);
    const remainingMonth = Number(lengthInMonths) - Number(vestsAfterInMonths);
    const newValue = remainingPercentage / remainingMonth;

    const quarterlyRemainingMonth = remainingMonth / 3;
    const quarterlyValue = remainingPercentage / quarterlyRemainingMonth;
    const semiAnnuallyRemainingMonth = remainingMonth / 6;
    const semiAnnuallyValue = remainingPercentage / semiAnnuallyRemainingMonth;
    const AnnuallyRemainingMonth = remainingMonth / 12;
    const AnnuallyValue = remainingPercentage / AnnuallyRemainingMonth;

    if (amountOneInPercentage) {
      if (value === 3) {
        setPercentage(Number(quarterlyValue));
      } else if (value === 6) {
        setPercentage(Number(semiAnnuallyValue));
      } else if (value === 12) {
        setPercentage(Number(AnnuallyValue));
      } else {
        setPercentage(Number(newValue));
      }
    } else {
      const nonPercentageValue = (Number(value) / Number(lengthInMonths)) * 100 ?? 0;
      setPercentage(Number(nonPercentageValue));
    }

    // setPercentage(Number(newValue));
  };

  const jsonValidation = Yup.string().test('is-json', 'Invalid JSON format', (value) => {
    if (!value) return true; // Allow empty values
    try {
      const parsed = JSON.parse(value);
      // Check if the parsed value is a number or array/object
      if (typeof parsed === 'number') {
        return false; // Reject numbers
      }
      // Ensure it's a valid JSON object or array
      return typeof parsed === 'object';
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    if (initialData.data as boolean) {
      const isData = vestingTypeOptions?.find(
        (item) => item.value === initialData.data?.vestingTypeId
      );
      if (isData?.isHybridVesting ?? false) {
        setValidationState({
          ...VESTING_TEMPLATES_VALIDATION,
          vestsAfterInMonths: Yup.number()
            .min(0, 'Must be 0 or greater')
            .max(100, 'Maximum characters exceeds')
            .required('Required field')
            .test('Digits only', 'Invalid Number', (value: any) => /^\d+$/.test(value))
            .nullable(),
          intervalInMonths: Yup.string().nullable().required('Interval is required.'),
        });
      }
      if (isData?.isStepVesting ?? false) {
        setValidationState({
          ...VESTING_TEMPLATES_VALIDATION,
          intervalInMonths: Yup.number()
            .min(1, 'Must be greater than 0')
            .max(100, 'Maximum characters exceeds')
            .required('Required field')
            .test('Digits only', 'Invalid Number', (value: any) => /^\d+$/.test(value))
            .nullable(),
        });
      }
      if (isData?.isCliffVesting ?? false) {
        setValidationState({
          ...VESTING_TEMPLATES_VALIDATION,
        });
      }
      if (isData?.isCliffVesting ?? false) {
        setValidationState({
          ...VESTING_TEMPLATES_VALIDATION,
        });
      }
      if (isData?.label === 'Custom Vesting') {
        setValidationState({
          ...VESTING_TEMPLATES_VALIDATION,
          intervalInMonths: commonValidation({ required: false }).nullable(),
        });
      }
      if (isData?.label === 'Specific Vesting') {
        setValidationState({
          ...VESTING_TEMPLATES_VALIDATION,
          intervalInMonths: commonValidation({ required: false }).nullable(),
          vestsAfterInMonths: commonValidation({
            required: false,
          }).nullable(),
          lengthInMonths: commonValidation({
            required: true,
            isNumber: true,
            max: 999,
            min: 0,
          }).test('Digits only', 'Must be whole number', (value: any) => /^\d+$/.test(value)),
          specificVestingInstruction: commonValidation({
            required: true,
          })
            .nullable()
            .concat(jsonValidation),
        });
      }
    }
  }, [initialData.data]);

  const { data: roundingTypeOptions } = useGetRoundingType(handleRequest);

  const [termPeriod, setTermPeriod] = useState(0);
  const [cliff, setCliff] = useState(0);
  const [stepSize, setStepSize] = useState<any>(0);

  const remainingDuration = termPeriod && cliff && termPeriod - cliff;
  const vestModulus = remainingDuration && stepSize && remainingDuration % stepSize;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...initialData.data,
        summary: initialData?.data?.summary === null ? '' : initialData?.data?.summary,
        amountOneInPercentage: initialData?.data?.amountOneInPercentage
          ? Number(initialData?.data?.amountOneInPercentage * 100).toFixed(ACCEPT_DECIMAL)
          : '',
      }}
      validationSchema={Yup.object().shape({
        ...validationState,
      })}
      onSubmit={(values) => {
        handleSubmit(values);
        // handleSubmit(window.sessionStorage.getItem('ToPreviewData'));
      }}>
      {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values }) => {
        const vestingTypeLabel = vestingTypeOptions?.find(
          (item) => item.value === values.vestingTypeId
        )?.label;
        return (
          <VestingModalWrapper id='vestingTemplateModalContainer'>
            <ModalComponent
              id='vestingTemplateModal'
              show={isModalOpen}
              closeModal={closeModal}
              loading={modalLoading}
              valueEdited={_.isEqual(values, {
                ...initialData.data,
                summary: initialData?.data?.summary === null ? '' : initialData?.data?.summary,
                amountOneInPercentage: initialData?.data?.amountOneInPercentage
                  ? Number(initialData?.data?.amountOneInPercentage * 100).toFixed(ACCEPT_DECIMAL)
                  : '',
              })}
              title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Vesting Template`}
              children={
                <Form id='vestingTemplateForm'>
                  <ModalBodyComponent id='vestingTemplateModalBody'>
                    <div
                      className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5'
                      id='vestingTemplateFields'>
                      <div
                        className='col-span-1 sm:col-span-2 lg:col-span-4'
                        id='vestingTemplateNameContainer'>
                        <InputText
                          type='text'
                          label='Vesting Template Name'
                          name='name'
                          id='name'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required
                        />
                      </div>
                      <div
                        className='col-span-1 sm:col-span-2 lg:col-span-4'
                        id='vestingTemplateDescriptionContainer'>
                        <CustomTextArea
                          label='Description'
                          name='summary'
                          id='summary'
                          onChange={handleChange}
                          errors={errors}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          touched={touched}
                          rows='5'
                        />
                      </div>
                      <div className='col-span-1 lg:col-span-2' id='vestingTemplateTypeContainer'>
                        <FormSelect
                          type='text'
                          label='Vesting Type'
                          name='vestingTypeId'
                          id='vestingTypeId'
                          viewName='vestingType'
                          onChange={(value: string) => {
                            const isData = vestingTypeOptions?.find((item) => item.value === value);
                            if (isData?.isHybridVesting ?? false) {
                              setValidationState({
                                ...VESTING_TEMPLATES_VALIDATION,
                                vestsAfterInMonths: Yup.number()
                                  .min(0, 'Must be 0 or greater')
                                  .max(100, 'Maximum characters exceeds')
                                  .required('Required field')
                                  .test('Digits only', 'Invalid Number', (value: any) =>
                                    /^\d+$/.test(value)
                                  )
                                  .nullable(),
                                intervalInMonths: Yup.string().nullable().required('Interval is required.'),
                              });
                            }
                            if (isData?.isStepVesting ?? false) {
                              setValidationState({
                                ...VESTING_TEMPLATES_VALIDATION,
                                intervalInMonths: Yup.number()
                                  .min(1, 'Must be greater than 0')
                                  .max(100, 'Maximum characters exceeds')
                                  .required('Required field')
                                  .test('Digits only', 'Invalid Number', (value: any) =>
                                    /^\d+$/.test(value)
                                  )
                                  .nullable(),
                              });
                            }
                            if (isData?.isCliffVesting ?? false) {
                              setValidationState({
                                ...VESTING_TEMPLATES_VALIDATION,
                                roundingTypeId: commonValidation({ required: false }).nullable(),
                              });
                            }
                            if (
                              !isData?.isCliffVesting &&
                              !isData?.isStepVesting &&
                              !isData?.isHybridVesting &&
                              isData?.label === 'Specific Vesting'
                            ) {
                              setValidationState({
                                ...VESTING_TEMPLATES_VALIDATION,
                                intervalInMonths: commonValidation({ required: false }).nullable(),
                                vestsAfterInMonths: commonValidation({
                                  required: false,
                                }).nullable(),
                                lengthInMonths: commonValidation({
                                  required: true,
                                  isNumber: true,
                                  max: 999,
                                  min: 0,
                                }).test('Digits only', 'Must be whole number', (value: any) =>
                                  /^\d+$/.test(value)
                                ),
                                specificVestingInstruction: commonValidation({
                                  required: true,
                                })
                                  .nullable()
                                  .concat(jsonValidation),
                                // periodStartDate: Yup.date()
                                //   .required('Required field')
                                //   .typeError('Required field'),
                              });
                            }
                            if (
                              !isData?.isCliffVesting &&
                              !isData?.isStepVesting &&
                              !isData?.isHybridVesting &&
                              isData?.label === 'Custom Vesting'
                            ) {
                              setValidationState({
                                ...VESTING_TEMPLATES_VALIDATION,
                                intervalInMonths: commonValidation({ required: true }).nullable(),
                              });
                            }
                            if (isData?.label === 'Custom Vesting Tranche') {
                              setValidationState({
                                ...VESTING_TEMPLATES_VALIDATION,
                                roundingTypeId: commonValidation({ required: false }),
                              });
                            }

                            setFieldValue('vestingType', value);
                            setFieldValue('vestingTypeId', value);
                            setFieldValue(
                              'lengthInMonths',
                              isData?.label === 'Specific Vesting' ||
                                isData?.label === 'Custom Vesting Tranche'
                                ? '1'
                                : ''
                            );
                            setFieldValue(
                              'roundingTypeId',
                              isData?.label === 'Custom Vesting Tranche' ? 1 : ''
                            );
                            setFieldValue(
                              'intervalInMonths',
                              isData?.label === 'Specific Vesting' ||
                                isData?.label === 'Custom Vesting Tranche'
                                ? '1'
                                : ''
                            );
                            // setFieldValue('intervalInMonths', '');
                            setFieldValue('periodStartDate', '');
                            setFieldValue('vestsAfterInMonths', '');
                            setFieldValue('amountOneInPercentage', '');
                            setFieldValue('loadingTypeId', null);
                            setTimeout(() => {
                              setFieldTouched('vestingTypeId', true);
                              // setFieldTouched('roundingTypeId', true);
                            });
                            setPercentageOne(0);
                            setPercentage(0);
                            setTermPeriod(0);
                            setCliff(0);
                            setStepSize(0);
                          }}
                          errors={errors}
                          touched={touched}
                          options={vestingTypeOptions}
                          values={values}
                          setFieldValue={setFieldValue}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required
                        />
                      </div>
                      {vestingTypeLabel !== 'Custom Vesting Tranche' && (
                        <div
                          className='col-span-1 lg:col-span-2'
                          id='vestingTemplateLoadingTypeContainer'>
                          <FormSelect
                            type='text'
                            label='Loading Type'
                            name='loadingTypeId'
                            id='loadingTypeId'
                            viewName='loadingType'
                            onChange={(value: string) => {
                              handleChange(value);
                            }}
                            errors={errors}
                            touched={touched}
                            options={loadingTypeOptions}
                            values={values}
                            setFieldValue={setFieldValue}
                            inputType={modalStatus}
                            disabled={
                              modalStatus === 'View' || vestingTypeLabel === 'Specific Vesting'
                            }
                            required
                          />
                        </div>
                      )}
                    </div>
                    {vestingTypeLabel !== 'Specific Vesting' &&
                      vestingTypeLabel !== 'Custom Vesting Tranche' && (
                        <div
                          className='grid grid-cols-2 gap-4'
                          id='vestingTemplateEndOfMonthSection'>
                          <FormSelect
                            type='text'
                            label='Vest At End Of Month'
                            name='endOfTheMonthTypeId'
                            id='endOfTheMonthTypeId'
                            viewName='loadingType'
                            onChange={(value: string) => {
                              handleChange(value);
                            }}
                            errors={errors}
                            touched={touched}
                            options={endOfMonthTypesOptions}
                            values={values}
                            setFieldValue={setFieldValue}
                            inputType={modalStatus}
                            disabled={modalStatus === 'View'}
                            required
                          />
                        </div>
                      )}
                    {vestingTypeLabel !== 'Specific Vesting' &&
                      vestingTypeLabel !== 'Custom Vesting Tranche' && (
                        <FormDivider id='vestingTemplateFormDivider' />
                      )}
                    {vestingTypeLabel === 'Specific Vesting' && (
                      <div
                        className='col-span-1 sm:col-span-2 lg:col-span-4'
                        id='vestingTemplateSpecificInstructionContainer'>
                        <CustomTextArea
                          label='Specific Vesting Instruction'
                          name='specificVestingInstruction'
                          id='specificVestingInstruction'
                          containerClassName='mb-0'
                          className='mb-0'
                          onChange={handleChange}
                          errors={errors}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          touched={touched}
                          required={true}
                          rows='5'
                        />
                      </div>
                    )}
                    {Boolean(values.vestingTypeId) &&
                      vestingTypeLabel !== 'Specific Vesting' &&
                      vestingTypeLabel !== 'Custom Vesting Tranche' && (
                        <div
                          className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'
                          id='vestingTemplateVestingDetailsSection'>
                          {!vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                            ?.isCliffVesting &&
                            !vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                              ?.isStepVesting &&
                            !vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                              ?.isHybridVesting && (
                              <div
                                className='col-span-1 lg:col-span-2'
                                id='vestingTemplatePeriodStartContainer'>
                                <CustomDatePicker
                                  label='Period Start Date'
                                  name='periodStartDate'
                                  id='periodStartDate'
                                  onChange={handleChange}
                                  arrValue={values?.periodStartDate}
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                  errors={errors}
                                  touched={touched}
                                  values={values}
                                  defaultValue={new Date()}
                                  required
                                  disabled={modalStatus === 'View'}
                                />
                              </div>
                            )}
                          <div
                            className='col-span-1 lg:col-span-2'
                            id='vestingTemplateTermPeriodContainer'>
                            <InputText
                              type='number'
                              label='Term Period'
                              name='lengthInMonths'
                              id='lengthInMonths'
                              onChange={(e: any) => {
                                const isData = vestingTypeOptions?.find(
                                  (item) => item.value === values.vestingTypeId
                                )?.isCliffVesting;

                                if (
                                  vestingTypeOptions?.find(
                                    (item) => item.value === values.vestingTypeId
                                  )?.isCliffVesting
                                ) {
                                  setFieldValue('vestsAfterInMonths', e.target.value);
                                  setFieldValue('amountOneInPercentage', 100);
                                  setFieldValue('intervalInMonths', 1);
                                }
                                setFieldValue('lengthInMonths', e.target.value);
                                if (
                                  !vestingTypeOptions?.find(
                                    (item) => item.value === values.vestingTypeId
                                  )?.isCliffVesting
                                ) {
                                  setFieldValue('intervalInMonths', '');
                                  setFieldValue('vestsAfterInMonths', '');
                                  setFieldValue('amountOneInPercentage', '');
                                }
                                setPercentageOne(0);
                                setPercentage(0);
                                setTermPeriod(e.target.value);
                              }}
                              errors={errors}
                              touched={touched}
                              inputType={modalStatus}
                              disabled={modalStatus === 'View'}
                              className={
                                vestingTypeOptions?.find(
                                  (item) => item.value === values.vestingTypeId
                                )?.isCliffVesting ?? false
                                  ? 'mb-0'
                                  : ''
                              }
                              width='104px'
                              required
                            />
                          </div>
                          {(vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                            ?.isCliffVesting ??
                            false) && (
                            <div
                              className='col-span-1 sm:col-span-2 lg:col-span-4 cliff-info'
                              id='vestingTemplateCliffInfo'>
                              <Heading
                                variant='body-s'
                                title='After the specified vesting period the vesting will end.'
                                className='info'
                                id='vestingTemplateCliffInfoText'
                              />
                            </div>
                          )}
                          {vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                            ?.isStepVesting && (
                            <div
                              className='col-span-1 lg:col-span-2'
                              id='vestingTemplateVestingFrequencyContainer'>
                              <InputText
                                type='number'
                                label={'Vesting Frequency'}
                                name='intervalInMonths'
                                id='intervalInMonths'
                                onChange={(e: any) => {
                                  if (
                                    vestingTypeOptions?.find(
                                      (item) => item.value === values.vestingTypeId
                                    )?.isStepVesting ??
                                    false
                                  ) {
                                    setValidationState({
                                      ...validationState,
                                      intervalInMonths: Yup.number()
                                        .min(0, 'Must be greater than 0')
                                        .max(
                                          Number(values.lengthInMonths) - 1,
                                          `Please enter a valid ${
                                            vestingTypeOptions?.find(
                                              (item) => item.value === values.vestingTypeId
                                            )?.isHybridVesting ?? false
                                              ? 'Cliffs'
                                              : 'Vesting Frequency'
                                          } less than the Term Period`
                                        )
                                        .required('Required field')
                                        .test('Digits only', 'Must be whole number', (value: any) =>
                                          /^\d+$/.test(value)
                                        )
                                        .test((value, testContext: Yup.TestContext) => {
                                          return Number(values.lengthInMonths) % Number(value) !== 0
                                            ? testContext.createError({
                                                message:
                                                  'Vesting Frequency is invalid, enter a number that is divisible to Term Period',
                                              })
                                            : true;
                                        })

                                        .nullable(),
                                    });
                                  }

                                  if (
                                    vestingTypeOptions?.find(
                                      (item) => item.value === values.vestingTypeId
                                    )?.isHybridVesting ??
                                    false
                                  ) {
                                    setValidationState({
                                      ...validationState,
                                      vestsAfterInMonths: Yup.number()
                                        .max(
                                          Number(values.lengthInMonths) - 1,
                                          `Please enter a valid ${
                                            vestingTypeOptions?.find(
                                              (item) => item.value === values.vestingTypeId
                                            )?.isHybridVesting ?? false
                                              ? 'Cliffs'
                                              : 'Vesting Frequency'
                                          } less than the Term Period`
                                        )
                                        .required('Required field')
                                        .test('Digits only', 'Must be whole number', (value: any) =>
                                          /^\d+$/.test(value)
                                        )
                                        .nullable(),
                                    });
                                  }

                                  setFieldValue('vestsAfterInMonths', e.target.value);
                                  setFieldValue('intervalInMonths', e.target.value);
                                  setCliff(e.target.value);

                                  handlePercentageOne(e.target.value, values.lengthInMonths);
                                  handlePercentageTwo(e.target.value, values.lengthInMonths);
                                  setTimeout(() => {
                                    setFieldTouched('vestsAfterInMonths', true);
                                  });
                                }}
                                errors={errors}
                                touched={touched}
                                inputType={modalStatus}
                                disabled={
                                  modalStatus === 'View' || !(values.lengthInMonths as boolean)
                                }
                                width='104px'
                                required
                              />
                            </div>
                          )}
                          {vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                            ?.isHybridVesting && (
                            <>
                              <div className='col-span-1 lg:col-span-1'>
                                <InputText
                                  type='number'
                                  label={`${
                                    vestingTypeOptions?.find(
                                      (item) => item.value === values.vestingTypeId
                                    )?.isHybridVesting ?? false
                                      ? 'Cliffs'
                                      : 'Vesting Frequency'
                                  }`}
                                  name='vestsAfterInMonths'
                                  id='vestsAfterInMonths'
                                  onChange={(e: any) => {
                                    if (
                                      vestingTypeOptions?.find(
                                        (item) => item.value === values.vestingTypeId
                                      )?.isStepVesting ??
                                      false
                                    ) {
                                      setValidationState({
                                        ...validationState,
                                        vestsAfterInMonths: Yup.number()
                                          .min(0, 'Must be greater than 0')
                                          .max(
                                            Number(values.lengthInMonths) - 1,
                                            `Please enter a valid ${
                                              vestingTypeOptions?.find(
                                                (item) => item.value === values.vestingTypeId
                                              )?.isHybridVesting ?? false
                                                ? 'Cliffs'
                                                : 'Vesting Frequency'
                                            } less than the Term Period`
                                          )
                                          .required('Required field')
                                          .test(
                                            'Digits only',
                                            'Must be whole number',
                                            (value: any) => /^\d+$/.test(value)
                                          )
                                          .test((value, testContext: Yup.TestContext) => {
                                            return Number(values.lengthInMonths) % Number(value) !==
                                              0
                                              ? testContext.createError({
                                                  message:
                                                    'Vesting Frequency is invalid, enter a number that is divisible to Term Period',
                                                })
                                              : true;
                                          })

                                          .nullable(),
                                      });
                                    }

                                    if (
                                      vestingTypeOptions?.find(
                                        (item) => item.value === values.vestingTypeId
                                      )?.isHybridVesting ??
                                      false
                                    ) {
                                      setValidationState({
                                        ...validationState,
                                        vestsAfterInMonths: Yup.number()
                                          .max(
                                            Number(values.lengthInMonths) - 1,
                                            `Please enter a valid ${
                                              vestingTypeOptions?.find(
                                                (item) => item.value === values.vestingTypeId
                                              )?.isHybridVesting ?? false
                                                ? 'Cliffs'
                                                : 'Vesting Frequency'
                                            } less than the Term Period`
                                          )
                                          .required('Required field')
                                          .test(
                                            'Digits only',
                                            'Must be whole number',
                                            (value: any) => /^\d+$/.test(value)
                                          )
                                          .nullable(),
                                      });
                                    }

                                    setFieldValue('vestsAfterInMonths', e.target.value);
                                    setFieldValue('intervalInMonths', '');
                                    setCliff(e.target.value);

                                    handlePercentageOne(e.target.value, values.lengthInMonths);
                                    setTimeout(() => {
                                      setFieldTouched('vestsAfterInMonths', true);
                                    });
                                  }}
                                  errors={errors}
                                  touched={touched}
                                  inputType={modalStatus}
                                  disabled={
                                    modalStatus === 'View' || !(values.lengthInMonths as boolean)
                                  }
                                  width='104px'
                                  required
                                />
                              </div>
                              <div className='col-span-1 lg:col-span-1'>
                                <InputText
                                  type='number'
                                  label={'Cliff Percent'}
                                  name='amountOneInPercentage'
                                  id='amountOneInPercentage'
                                  onChange={(e: any) => {
                                    handleChange(e);
                                    handlePercentageThree(
                                      values?.intervalInMonths,
                                      values.lengthInMonths,
                                      e.target.value,
                                      values?.vestsAfterInMonths
                                    );
                                  }}
                                  errors={errors}
                                  touched={touched}
                                  inputType={modalStatus}
                                  width='104px'
                                  disabled={modalStatus === 'View'}
                                  required
                                />
                              </div>
                              <div className='col-span-1 lg:col-span-2'>
                                {(vestingTypeOptions?.find(
                                  (item) => item.value === values.vestingTypeId
                                )?.isHybridVesting ??
                                  false) && (
                                  <FormSelect
                                    label='Vesting Frequency'
                                    name='intervalInMonths'
                                    id='intervalInMonths'
                                    viewName='intervalInMonths'
                                    onChange={(value: string) => {
                                      setFieldValue('intervalInMonths', value);
                                      setStepSize(value);
                                      handlePercentageThree(
                                        Number(value),
                                        values?.lengthInMonths,
                                        values?.amountOneInPercentage,
                                        values?.vestsAfterInMonths
                                      );
                                      // handlePercentageTwo(value, values.lengthInMonths);
                                    }}
                                    errors={errors}
                                    touched={touched}
                                    options={VestingFrequencyOptions.filter((item) => {
                                      return (
                                        (Number(values.lengthInMonths) -
                                          Number(values.vestsAfterInMonths)) %
                                          Number(item.value) ===
                                        0
                                      );
                                    })}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    inputType={modalStatus}
                                    disabled={
                                      modalStatus === 'View'
                                        ? modalStatus === 'View'
                                        : typeof values.vestsAfterInMonths !== 'number'
                                        ? !(values.vestsAfterInMonths as boolean)
                                        : !(Number(values.vestsAfterInMonths) >= 0)
                                    }
                                    required
                                  />
                                )}
                              </div>
                            </>
                          )}
                          {!vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                            ?.isCliffVesting &&
                            !vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                              ?.isStepVesting &&
                            !vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                              ?.isHybridVesting && (
                              <>
                                <div className='col-span-1 lg:col-span-1'>
                                  <InputText
                                    type='number'
                                    label={'Cliff Percent'}
                                    name='amountOneInPercentage'
                                    id='amountOneInPercentage'
                                    onChange={(e: any) => {
                                      handleChange(e);
                                      handlePercentageThree(
                                        values?.intervalInMonths,
                                        values.lengthInMonths,
                                        values?.amountOneInPercentage,
                                        values?.vestsAfterInMonths
                                      );
                                    }}
                                    errors={errors}
                                    touched={touched}
                                    inputType={modalStatus}
                                    width='104px'
                                    disabled={modalStatus === 'View'}
                                    required
                                  />
                                </div>
                                <div className='col-span-1 lg:col-span-2'>
                                  {/* {modalStatus === 'Edit' || modalStatus === 'View' ? (
                                  VestingFrequencyOptions.filter((item) => {
                                    return (
                                      (Number(values.lengthInMonths) -
                                        Number(values.vestsAfterInMonths)) %
                                        Number(item.value) ===
                                      0
                                    );
                                  }).some((item) => item.value === values?.intervalInMonths) ? (
                                    <FormSelect
                                      label='Vesting Frequency'
                                      name='intervalInMonths'
                                      viewName='intervalInMonths'
                                      onChange={(value: string) => {
                                        setFieldValue('intervalInMonths', value);
                                        handlePercentageTwo(value, values.lengthInMonths);
                                        handlePercentageThree(
                                          Number(value),
                                          values.lengthInMonths,
                                          values?.amountOneInPercentage,
                                          values?.vestsAfterInMonths
                                        );
                                      }}
                                      errors={errors}
                                      touched={touched}
                                      options={VestingFrequencyOptions.filter((item) => {
                                        return (
                                          (Number(values.lengthInMonths) -
                                            Number(values.vestsAfterInMonths)) %
                                            Number(item.value) ===
                                          0
                                        );
                                      })}
                                      values={values}
                                      setFieldValue={setFieldValue}
                                      inputType={modalStatus}
                                      disabled={
                                        modalStatus === 'View' || values?.lengthInMonths === ''
                                      }
                                      required
                                    />
                                  ) : (
                                    <InputText
                                      type='number'
                                      label={'Vesting Frequency (Month)'}
                                      name='intervalInMonths'
                                      id='intervalInMonths'
                                      onChange={(e: any) => {
                                        if (
                                          vestingTypeOptions?.find(
                                            (item) => item.value === values.vestingTypeId
                                          )?.isStepVesting ??
                                          false
                                        ) {
                                          setValidationState({
                                            ...validationState,
                                            intervalInMonths: Yup.number()
                                              .min(0, 'Must be greater than 0')
                                              .max(
                                                Number(values.lengthInMonths) - 1,
                                                `Please enter a valid ${
                                                  vestingTypeOptions?.find(
                                                    (item) => item.value === values.vestingTypeId
                                                  )?.isHybridVesting ?? false
                                                    ? 'Cliffs'
                                                    : 'Vesting Frequency'
                                                } (Month) less than the Term Period`
                                              )
                                              .required('Required field')
                                              .test(
                                                'Digits only',
                                                'Must be whole number',
                                                (value: any) => /^\d+$/.test(value)
                                              )
                                              .test((value, testContext: Yup.TestContext) => {
                                                return Number(values.lengthInMonths) %
                                                  Number(value) !==
                                                  0
                                                  ? testContext.createError({
                                                      message:
                                                        'Vesting Frequency is invalid, enter a number that is divisible to Term Period',
                                                    })
                                                  : true;
                                              })

                                              .nullable(),
                                          });
                                        }

                                        if (
                                          vestingTypeOptions?.find(
                                            (item) => item.value === values.vestingTypeId
                                          )?.isHybridVesting ??
                                          false
                                        ) {
                                          setValidationState({
                                            ...validationState,
                                            vestsAfterInMonths: Yup.number()
                                              .max(
                                                Number(values.lengthInMonths) - 1,
                                                `Please enter a valid ${
                                                  vestingTypeOptions?.find(
                                                    (item) => item.value === values.vestingTypeId
                                                  )?.isHybridVesting ?? false
                                                    ? 'Cliffs'
                                                    : 'Vesting Frequency'
                                                } (Month) less than the Term Period`
                                              )
                                              .required('Required field')
                                              .test(
                                                'Digits only',
                                                'Must be whole number',
                                                (value: any) => /^\d+$/.test(value)
                                              )
                                              .nullable(),
                                          });
                                        }

                                        setFieldValue('vestsAfterInMonths', e.target.value);
                                        setFieldValue('intervalInMonths', e.target.value);
                                        setCliff(e.target.value);

                                        handlePercentageOne(e.target.value, values.lengthInMonths);
                                        handlePercentageTwo(e.target.value, values.lengthInMonths);
                                        setTimeout(() => {
                                          setFieldTouched('vestsAfterInMonths', true);
                                        });
                                      }}
                                      errors={errors}
                                      touched={touched}
                                      inputType={modalStatus}
                                      disabled={
                                        modalStatus === 'View' ||
                                        !(values.lengthInMonths as boolean)
                                      }
                                      width='104px'
                                      required
                                    />
                                  )
                                ) : ( */}
                                  <FormSelect
                                    label='Vesting Frequency'
                                    name='intervalInMonths'
                                    id='intervalInMonths'
                                    viewName='intervalInMonths'
                                    onChange={(value: string) => {
                                      setFieldValue('intervalInMonths', value);
                                      handlePercentageTwo(value, values.lengthInMonths);
                                      handlePercentageThree(
                                        Number(value),
                                        values.lengthInMonths,
                                        values?.amountOneInPercentage,
                                        values?.vestsAfterInMonths
                                      );
                                    }}
                                    errors={errors}
                                    touched={touched}
                                    options={VestingFrequencyOptions.filter((item) => {
                                      return (
                                        (Number(values.lengthInMonths) -
                                          Number(values.vestsAfterInMonths)) %
                                          Number(item.value) ===
                                        0
                                      );
                                    })}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    inputType={modalStatus}
                                    disabled={
                                      modalStatus === 'View' || values?.lengthInMonths === ''
                                    }
                                    required
                                  />
                                  {/* )} */}
                                </div>
                              </>
                            )}
                        </div>
                      )}
                    {Boolean(values.vestingTypeId) &&
                      !vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                        ?.isCliffVesting && (
                        <>
                          {vestingTypeLabel !== 'Custom Vesting Tranche' && <FormDivider />}
                          {vestingTypeLabel !== 'Custom Vesting Tranche' && (
                            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                              <div className='col-span-1 lg:col-span-2'>
                                <FormSelect
                                  label='Rounding Type'
                                  name='roundingTypeId'
                                  id='roundingTypeId'
                                  viewName='roundingTypeId'
                                  onChange={(value: string) => {
                                    setFieldValue('roundingTypeId', value);
                                  }}
                                  errors={errors}
                                  touched={touched}
                                  errorValue={errors?.roundingTypeId}
                                  touchedValue={touched?.roundingTypeId}
                                  options={roundingTypeOptions}
                                  values={values}
                                  setFieldValue={setFieldValue}
                                  inputType={modalStatus}
                                  disabled={modalStatus === 'View'}
                                  required
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    {(vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                      ?.isCliffVesting ??
                      false) &&
                    Number(values.lengthInMonths) > 0 &&
                    !(errors.lengthInMonths as unknown as boolean) ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                        <div className='col-span-1 sm:col-span-2 lg:col-span-4'>
                          <ViewUploadedDoc
                            variant='primary'
                            size='lg'
                            children={
                              <>
                                <div className='vest-info'>
                                  <Heading variant='body' title='100%' className='info' />
                                  <Heading
                                    variant='body'
                                    title={`of shares will vest after ${Number(
                                      values.lengthInMonths
                                    )} month${Number(values.lengthInMonths) > 1 ? 's' : ''}`}
                                  />
                                </div>
                              </>
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                    {(vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                      ?.isCliffVesting ??
                      false) &&
                    (values.lengthInMonths as boolean) &&
                    (errors.lengthInMonths as unknown as boolean) &&
                    (touched.lengthInMonths as boolean) ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                        <div className='col-span-1 sm:col-span-2 lg:col-span-4'>
                          <ViewUploadedDoc
                            variant='primary'
                            size='lg'
                            children={
                              <div className='vest-info'>
                                <Heading
                                  variant='body'
                                  title='Invalid Data. Please try again.'
                                  className='info'
                                />
                              </div>
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                    {vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                      ?.isStepVesting &&
                    Number(values.lengthInMonths) > 0 &&
                    Number(values.intervalInMonths) >= 0 &&
                    !(errors.intervalInMonths as unknown as boolean) ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                        <div className='col-span-1 sm:col-span-2 lg:col-span-4'>
                          <ViewUploadedDoc
                            variant='primary'
                            size='lg'
                            children={
                              <>
                                <div className='vest-info'>
                                  <Heading
                                    variant='body'
                                    title={`${percentage.toFixed(ACCEPT_DECIMAL)}%`}
                                    className='info'
                                  />
                                  <Heading
                                    variant='body'
                                    title={`vest ${
                                      vestingTypeOptions?.find(
                                        (item) => item.value === values.vestingTypeId
                                      )?.isHybridVesting ?? false
                                        ? 'after the first'
                                        : 'every'
                                    } ${String(values.intervalInMonths ?? '')} month${
                                      Number(values.intervalInMonths) > 1 ? 's' : ''
                                    }`}
                                  />
                                </div>
                              </>
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                    {vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                      ?.isHybridVesting &&
                    Number(values.lengthInMonths) > 0 &&
                    Number(values.vestsAfterInMonths) >= 0 &&
                    !(errors.vestsAfterInMonths as unknown as boolean) ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                        <div className='col-span-1 sm:col-span-2 lg:col-span-4'>
                          <ViewUploadedDoc
                            variant='primary'
                            size='lg'
                            children={
                              <>
                                <div className='vest-info'>
                                  <Heading
                                    variant='body'
                                    title={`${
                                      values?.amountOneInPercentage
                                        ? Number(values?.amountOneInPercentage).toFixed(
                                            ACCEPT_DECIMAL
                                          )
                                        : percentageOne.toFixed(ACCEPT_DECIMAL)
                                    }%`}
                                    className='info'
                                  />
                                  <Heading
                                    variant='body'
                                    title={`vest ${
                                      vestingTypeOptions?.find(
                                        (item) => item.value === values.vestingTypeId
                                      )?.isHybridVesting ?? false
                                        ? 'after the first'
                                        : 'every'
                                    } ${String(values.vestsAfterInMonths ?? '')} month${
                                      Number(values.vestsAfterInMonths) > 1 ? 's' : ''
                                    }`}
                                  />
                                </div>
                                {(values.intervalInMonths as boolean) && (
                                  <div className='vest-info'>
                                    <Heading
                                      variant='body'
                                      title={`${percentage.toFixed(ACCEPT_DECIMAL)}%`}
                                      className='info'
                                    />
                                    <Heading
                                      variant='body'
                                      title={`vest every ${String(
                                        values.intervalInMonths ?? ''
                                      )} month${
                                        Number(values.intervalInMonths) > 1 ? 's' : ''
                                      } thereafter`}
                                    />
                                  </div>
                                )}
                              </>
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                    {!vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                      ?.isCliffVesting &&
                    !vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                      ?.isStepVesting &&
                    !vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                      ?.isHybridVesting &&
                    Number(values.lengthInMonths) > 0 &&
                    Number(values.amountOneInPercentage) > 0 ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                        <div className='col-span-1 sm:col-span-2 lg:col-span-4'>
                          <ViewUploadedDoc
                            variant='primary'
                            size='lg'
                            children={
                              <>
                                {/* <div className='vest-info'>
                                  <Heading
                                    variant='body'
                                    title={`${values?.amountOneInPercentage}%`}
                                    className='info'
                                  />
                                  <Heading
                                    variant='body'
                                    title={`Vest ${
                                      vestingTypeOptions?.find(
                                        (item) => item.value === values.vestingTypeId
                                      )?.isHybridVesting ?? false
                                        ? 'after the first'
                                        : 'every'
                                    } ${String(values.vestsAfterInMonths ?? '')} month${
                                      Number(values.vestsAfterInMonths) > 1 ? 's' : ''
                                    }`}
                                  />
                                </div> */}
                                {(values.intervalInMonths as boolean) &&
                                  vestingTypeOptions?.find(
                                    (item) => item.value === values.vestingTypeId
                                  )?.label !== 'Custom Vesting' && (
                                    <div className='vest-info'>
                                      <Heading
                                        variant='body'
                                        title={`${values?.amountOneInPercentage}%`}
                                        className='info'
                                      />
                                      <Heading
                                        variant='body'
                                        title={
                                          values?.periodStartDate !== 'Invalid date' &&
                                          values?.periodStartDate
                                            ? ` on ${moment(values?.periodStartDate).format(
                                                DATE_FORMAT
                                              )}`
                                            : 'vesting start date'
                                        }
                                        // className='info'
                                      />
                                    </div>
                                  )}
                                {(values.intervalInMonths as boolean) && (
                                  <div className='vest-info'>
                                    <Heading
                                      variant='body'
                                      title={`Vest every ${String(
                                        values.intervalInMonths ?? ''
                                      )} month${
                                        Number(values.intervalInMonths) > 1 ? 's' : ''
                                      } thereafter`}
                                    />
                                  </div>
                                )}
                              </>
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                    {!(
                      vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                        ?.isCliffVesting ?? false
                    ) &&
                    Number(values.lengthInMonths) > 0 &&
                    (Number(values.vestsAfterInMonths) as unknown as boolean) &&
                    (errors.vestsAfterInMonths as unknown as boolean) ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                        <div className='col-span-1 sm:col-span-2 lg:col-span-4'>
                          <ViewUploadedDoc
                            variant='primary'
                            size='lg'
                            children={
                              <div className='vest-info'>
                                <Heading
                                  variant='body'
                                  title='Invalid Data. Please try again.'
                                  className='info'
                                />
                              </div>
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                    {vestModulus < 0 ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                        <div className='col-span-1 sm:col-span-2 lg:col-span-4'>
                          <ViewUploadedDoc
                            variant='primary'
                            size='lg'
                            children={
                              <div className='vest-info'>
                                <Heading
                                  variant='body'
                                  title='Invalid Data. Please try again.'
                                  className='info'
                                />
                              </div>
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                    {(vestingTypeOptions?.find((item) => item.value === values.vestingTypeId)
                      ?.isStepVesting ??
                      false) &&
                    Number(values.lengthInMonths) > 0 &&
                    (Number(values.intervalInMonths) as unknown as boolean) &&
                    (errors.intervalInMonths as unknown as boolean) ? (
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 form-gap'>
                        <div className='col-span-1 sm:col-span-2 lg:col-span-4'>
                          <ViewUploadedDoc
                            variant='primary'
                            size='lg'
                            children={
                              <div className='vest-info'>
                                <Heading
                                  variant='body'
                                  title='Invalid Data. Please try again.'
                                  className='info'
                                />
                              </div>
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                  </ModalBodyComponent>
                  <ModalFooterComponent
                    id='vestingTemplateModalFooter'
                    modalStatus={modalStatus}
                    loading={isLoading}
                    onCloseModal={() => {
                      closeModal();
                    }}
                    valueChanged={_.isEqual(values, {
                      ...initialData.data,
                      summary:
                        initialData?.data?.summary === null ? '' : initialData?.data?.summary,
                      amountOneInPercentage: initialData?.data?.amountOneInPercentage
                        ? Number(initialData?.data?.amountOneInPercentage * 100).toFixed(
                            ACCEPT_DECIMAL
                          )
                        : '',
                    })}
                    onEditModal={() => {
                      rowData?.isEditable ? setModalStatus('Edit') : '';
                    }}
                  />
                </Form>
              }
            />
          </VestingModalWrapper>
        );
      }}
    </Formik>
  );
};

export default NewVestingModal;
